import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import AdminAcademicPanel from '../components/AdminAcademicPanel';

export default function AdminPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classStats, setClassStats] = useState({});
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentStats, setStudentStats] = useState({});
  const [allStudents, setAllStudents] = useState([]);
  const [allParents, setAllParents] = useState([]);
  const [parentNumbers, setParentNumbers] = useState([]); // Array of { phoneNumber, students: [] }
  const [expandedParentNumbers, setExpandedParentNumbers] = useState(new Set());
  const [nonStudentUsers, setNonStudentUsers] = useState([]); // Admin and instructor users
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userEditData, setUserEditData] = useState({});
  const [editingClass, setEditingClass] = useState(null);
  const [showEditClassModal, setShowEditClassModal] = useState(false);
  const [classEditData, setClassEditData] = useState({});
  const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'students'
  const [activeTab, setActiveTab] = useState('academic'); // 'academic', 'classes', 'students', 'parents', 'users'
  const [showStudentSummary, setShowStudentSummary] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingStudent, setEditingStudent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check user authentication and role
    const authData = sessionStorage.getItem('user_auth');
    if (!authData) {
      navigate('/login');
      return;
    }

    const { user } = JSON.parse(authData);
    setUserData(user);

    // Check if user is admin
    if (user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    // If user is admin, fetch data
    fetchAllClasses();
    fetchAllStudents();
    fetchAllParents();
    fetchNonStudentUsers();
    setLoading(false);
  }, [navigate]);

  const fetchAllStudents = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          id,
          name,
          email,
          code,
          phone_number,
          date_of_birth,
          created_at,
          updated_at
        `)
        .eq('role', 'student')
        .order('name', { ascending: true });

      if (error) {
        console.error('Supabase error fetching students:', error);
        throw error;
      }
      
      setAllStudents(data || []);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError(`Failed to fetch students: ${err.message}`);
    }
  };

  const fetchNonStudentUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          id,
          name,
          email,
          role,
          code,
          phone_number,
          created_at,
          updated_at
        `)
        .in('role', ['admin', 'instructor'])
        .order('role', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setNonStudentUsers(data || []);
    } catch (err) {
      console.error('Error fetching non-student users:', err);
    }
  };

  const fetchAllParents = async () => {
    try {
      // Fetch all students with their parent numbers
      const { data: studentsData, error } = await supabase
        .from('users')
        .select(`
          id,
          name,
          email,
          code,
          parent1_number,
          parent2_number
        `)
        .eq('role', 'student')
        .order('name', { ascending: true });

      if (error) throw error;

      // Create a map to group students by parent number
      const parentMap = new Map();

      studentsData.forEach(student => {
        // Add student to parent1_number if it exists
        if (student.parent1_number) {
          const phone = student.parent1_number.trim();
          if (!parentMap.has(phone)) {
            parentMap.set(phone, []);
          }
          const existing = parentMap.get(phone).find(s => s.id === student.id);
          if (existing) {
            // Student already exists, update parentType to show both
            existing.parentType = existing.parentType === 'parent2' ? 'Both' : 'parent1';
          } else {
            parentMap.get(phone).push({ ...student, parentType: 'parent1' });
          }
        }

        // Add student to parent2_number if it exists
        if (student.parent2_number) {
          const phone = student.parent2_number.trim();
          if (!parentMap.has(phone)) {
            parentMap.set(phone, []);
          }
          const existing = parentMap.get(phone).find(s => s.id === student.id);
          if (existing) {
            // Student already exists, update parentType to show both
            existing.parentType = existing.parentType === 'parent1' ? 'Both' : 'parent2';
          } else {
            parentMap.get(phone).push({ ...student, parentType: 'parent2' });
          }
        }
      });

      // Convert map to array and sort by phone number
      const parentNumbersArray = Array.from(parentMap.entries())
        .map(([phoneNumber, students]) => ({
          phoneNumber,
          students: students.sort((a, b) => a.name.localeCompare(b.name))
        }))
        .sort((a, b) => a.phoneNumber.localeCompare(b.phoneNumber));

      setParentNumbers(parentNumbersArray);
      
      // Keep the old parent users fetch for backward compatibility
      const { data: parentUsers, error: parentError } = await supabase
        .from('users')
        .select(`
          id,
          name,
          email,
          phone_number,
          created_at,
          updated_at
        `)
        .eq('role', 'parent')
        .order('name', { ascending: true });

      if (!parentError) {
        setAllParents(parentUsers || []);
      }
    } catch (err) {
      console.error('Error fetching parents:', err);
    }
  };

  const fetchAllClasses = async () => {
    try {
      setLoading(true);
      
      // Fetch all levels/classes with teacher information
      const { data: levelsData, error: levelsError } = await supabase
        .from('levels')
        .select(`
          *,
          primary_teacher:users!levels_teacher_id_fkey(id, name),
          secondary_teacher:users!levels_secondary_teacher_id_fkey(id, name)
        `)
        .order('name', { ascending: true });

      if (levelsError) throw levelsError;

      // Fetch stats for each class
      const statsPromises = levelsData.map(async (level) => {
        const stats = await fetchClassStats(level.code);
        return { ...level, stats };
      });

      const classesWithStats = await Promise.all(statsPromises);
      setClasses(classesWithStats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentsForClass = async (levelCode) => {
    try {
      // Fetch students enrolled in this class
      const { data: studentsData, error: studentsError } = await supabase
        .from('student_enrollment')
        .select(`
          student:users (
            id,
            name,
            email,
            code
          )
        `)
        .eq('level_code', levelCode);

      if (studentsError) throw studentsError;

      const studentsList = studentsData.map(item => item.student);
      // Sort students by name alphabetically
      const sortedStudents = studentsList.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setStudents(sortedStudents);

      // Fetch individual student stats
      const statsPromises = sortedStudents.map(async (student) => {
        const stats = await fetchStudentStats(student.id, levelCode);
        return { studentId: student.id, stats };
      });

      const studentStatsData = await Promise.all(statsPromises);
      const statsObject = {};
      studentStatsData.forEach(({ studentId, stats }) => {
        statsObject[studentId] = stats;
      });
      setStudentStats(statsObject);
    } catch (err) {
      console.error(`Error fetching students for ${levelCode}:`, err);
      setError(err.message);
    }
  };

  const fetchStudentStats = async (studentId, levelCode) => {
    try {
      // Fetch student grades
      const { data: gradesData, error: gradesError } = await supabase
        .from('grades')
        .select(`
          score,
          assignment:assignments!inner(max_score, level_code)
        `)
        .eq('student_id', studentId)
        .eq('assignment.level_code', levelCode);

      if (gradesError) throw gradesError;

      // Calculate grade average
      let gradeAverage = 0;
      if (gradesData.length > 0) {
        const totalScore = gradesData.reduce((sum, grade) => sum + grade.score, 0);
        const maxPossible = gradesData.reduce((sum, grade) => sum + grade.assignment.max_score, 0);
        gradeAverage = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;
      }

      // Fetch student attendance
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('attendance')
        .select('status')
        .eq('student_id', studentId)
        .eq('level_code', levelCode);

      if (attendanceError) throw attendanceError;

      // Calculate attendance stats
      const attendanceStats = {
        present: 0,
        absent: 0,
        late: 0,
        excused: 0,
        total: attendanceData.length
      };

      attendanceData.forEach(record => {
        if (record.status) {
          attendanceStats[record.status] = (attendanceStats[record.status] || 0) + 1;
        }
      });

      // Calculate attendance percentage
      const weightedAttendance = attendanceStats.present + attendanceStats.excused + (attendanceStats.late * 0.5);
      const attendancePercentage = attendanceStats.total > 0 ? Math.round((weightedAttendance / attendanceStats.total) * 100) : 0;

      return {
        gradeAverage,
        attendanceStats,
        attendancePercentage,
        totalGrades: gradesData.length,
        totalAttendance: attendanceData.length
      };
    } catch (err) {
      console.error(`Error fetching stats for student ${studentId}:`, err);
      return {
        gradeAverage: 0,
        attendanceStats: { present: 0, absent: 0, late: 0, excused: 0, total: 0 },
        attendancePercentage: 0,
        totalGrades: 0,
        totalAttendance: 0
      };
    }
  };

  const fetchClassStats = async (levelCode) => {
    try {
      // Fetch student count
      const { data: studentsData, error: studentsError } = await supabase
        .from('student_enrollment')
        .select('student_id')
        .eq('level_code', levelCode);

      if (studentsError) throw studentsError;
      const studentCount = studentsData.length;

      // Fetch assignment count
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('assignments')
        .select('id, max_score')
        .eq('level_code', levelCode);

      if (assignmentsError) throw assignmentsError;
      const assignmentCount = assignmentsData.length;

      // Fetch grades for average calculation
      const { data: gradesData, error: gradesError } = await supabase
        .from('grades')
        .select(`
          score,
          assignment:assignments!inner(max_score, level_code)
        `)
        .eq('assignment.level_code', levelCode);

      if (gradesError) throw gradesError;

      // Calculate class average
      let classAverage = 0;
      if (gradesData.length > 0) {
        const totalScore = gradesData.reduce((sum, grade) => sum + grade.score, 0);
        const maxPossible = gradesData.reduce((sum, grade) => sum + grade.assignment.max_score, 0);
        classAverage = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;
      }

      // Fetch attendance data
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('attendance')
        .select('status')
        .eq('level_code', levelCode);

      if (attendanceError) throw attendanceError;

      // Calculate attendance stats
      const attendanceStats = {
        present: 0,
        absent: 0,
        late: 0,
        excused: 0,
        total: attendanceData.length
      };

      attendanceData.forEach(record => {
        if (record.status) {
          attendanceStats[record.status] = (attendanceStats[record.status] || 0) + 1;
        }
      });

      // Calculate attendance percentage (present + excused = full, late = 0.5)
      const weightedAttendance = attendanceStats.present + attendanceStats.excused + (attendanceStats.late * 0.5);
      const attendancePercentage = attendanceStats.total > 0 ? Math.round((weightedAttendance / attendanceStats.total) * 100) : 0;

      return {
        studentCount,
        assignmentCount,
        classAverage,
        attendanceStats,
        attendancePercentage,
        totalGrades: gradesData.length
      };
    } catch (err) {
      console.error(`Error fetching stats for ${levelCode}:`, err);
      return {
        studentCount: 0,
        assignmentCount: 0,
        classAverage: 0,
        attendanceStats: { present: 0, absent: 0, late: 0, excused: 0, total: 0 },
        attendancePercentage: 0,
        totalGrades: 0
      };
    }
  };

  const getClassAverageColor = (average) => {
    if (average >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (average >= 70) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getAttendanceColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const handleClassSelect = async (classItem) => {
    setSelectedClass(classItem);
    setShowStudentSummary(true);
    setLoading(true);
    try {
      await fetchStudentsForClass(classItem.code);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToOverview = () => {
    setShowStudentSummary(false);
    setSelectedClass(null);
    setStudents([]);
    setStudentStats({});
  };

  const getStudentEnrollments = async (studentId) => {
    try {
      const { data, error } = await supabase
        .from('student_enrollment')
        .select(`
          level_code,
          level:levels(name, code)
        `)
        .eq('student_id', studentId);

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching student enrollments:', err);
      return [];
    }
  };

  const handleStudentUpdate = async (updatedData) => {
    setIsSubmitting(true);
    try {
      // Clean the data - convert empty strings to null for date fields
      const cleanedData = {
        ...updatedData,
        date_of_birth: updatedData.date_of_birth || null,
        phone_number: updatedData.phone_number || null,
        code: updatedData.code || null
      };

      const { error } = await supabase
        .from('users')
        .update(cleanedData)
        .eq('id', editingStudent.id);

      if (error) throw error;
      await fetchAllStudents();
      setShowEditModal(false);
      setEditingStudent(null);
      setEditData({});
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (student) => {
    setEditingStudent(student);
    setEditData({
      name: student.name || '',
      email: student.email || '',
      code: student.code || '',
      phone_number: student.phone_number || '',
      date_of_birth: student.date_of_birth ? student.date_of_birth.split('T')[0] : '' // Format date for input
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingStudent(null);
    setEditData({});
  };

  const toggleParentNumber = (phoneNumber) => {
    setExpandedParentNumbers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phoneNumber)) {
        newSet.delete(phoneNumber);
      } else {
        newSet.add(phoneNumber);
      }
      return newSet;
    });
  };

  const handleAddUser = async (userData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('users')
        .insert([{
          ...userData,
          code: userData.code || null,
          phone_number: userData.phone_number || null
        }]);

      if (error) throw error;
      await fetchNonStudentUsers();
      setShowAddUserModal(false);
      setUserEditData({});
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (userData) => {
    setIsSubmitting(true);
    try {
      const cleanedData = {
        ...userData,
        code: userData.code || null,
        phone_number: userData.phone_number || null
      };

      const { error } = await supabase
        .from('users')
        .update(cleanedData)
        .eq('id', editingUser.id);

      if (error) throw error;
      await fetchNonStudentUsers();
      setShowEditUserModal(false);
      setEditingUser(null);
      setUserEditData({});
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditUserModal = (user) => {
    setEditingUser(user);
    setUserEditData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || '',
      code: user.code || '',
      phone_number: user.phone_number || ''
    });
    setShowEditUserModal(true);
  };

  const closeEditUserModal = () => {
    setShowEditUserModal(false);
    setEditingUser(null);
    setUserEditData({});
  };

  const handleUpdateClass = async (classData) => {
    setIsSubmitting(true);
    try {
      const updateData = {
        name: classData.name,
        period: classData.period || null,
        active: classData.active !== undefined ? classData.active : true,
        pass_code: classData.pass_code || null,
        teacher_id: classData.teacher_id || null,
        secondary_teacher_id: classData.secondary_teacher_id || null
      };

      const { error } = await supabase
        .from('levels')
        .update(updateData)
        .eq('code', editingClass.code);

      if (error) throw error;
      await fetchAllClasses();
      setShowEditClassModal(false);
      setEditingClass(null);
      setClassEditData({});
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditClassModal = (classItem) => {
    setEditingClass(classItem);
    setClassEditData({
      name: classItem.name || '',
      period: classItem.period || '',
      code: classItem.code || '',
      active: classItem.active !== undefined ? classItem.active : true,
      pass_code: classItem.pass_code || '',
      teacher_id: classItem.teacher_id || '',
      secondary_teacher_id: classItem.secondary_teacher_id || ''
    });
    setShowEditClassModal(true);
  };

  const closeEditClassModal = () => {
    setShowEditClassModal(false);
    setEditingClass(null);
    setClassEditData({});
  };

  const filteredStudents = allStudents.filter(student =>
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredParents = allParents.filter(parent =>
    parent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    parent.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredParentNumbers = parentNumbers.filter(parentGroup =>
    parentGroup.phoneNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    parentGroup.students.some(student => 
      student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.code?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-neutral-900 mb-2">
            {error.includes('Access Denied') ? 'Access Denied' : 'Error Loading Dashboard'}
          </h3>
          <p className="text-neutral-500 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="btn-primary"
            >
              Go to Dashboard
            </button>
            {!error.includes('Access Denied') && (
              <button
                onClick={() => window.location.reload()}
                className="btn-secondary"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100">
      {/* Simple Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-neutral-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-lg font-bold text-neutral-900">Admin Dashboard</h1>
                <p className="text-xs text-neutral-500">
                {activeTab === 'academic' && 'Term classes & transcripts'}
                {activeTab === 'classes' && `${classes.length} Classes`}
                {activeTab === 'students' && `${allStudents.length} Students`}
                  {activeTab === 'parents' && `${parentNumbers.length} Parents`}
                  {activeTab === 'users' && `${nonStudentUsers.length} Users`}
                </p>
          </div>
        </div>
      </div>

          {/* Scrollable Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {[
              { id: 'academic', label: 'Term classes', icon: '📚' },
              { id: 'transcripts', label: 'Transcripts', icon: '✅' },
              { id: 'classes', label: 'Past levels', icon: '🏫', count: classes.length },
              { id: 'students', label: 'Students', icon: '👥', count: allStudents.length },
              { id: 'parents', label: 'Parents', icon: '👨‍👩‍👧', count: parentNumbers.length },
              { id: 'users', label: 'Users', icon: '👤', count: nonStudentUsers.length }
            ].map((tab) => (
            <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'transcripts') {
                    navigate('/dashboard/transcript')
                    return
                  }
                  setActiveTab(tab.id)
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-100 text-neutral-600'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                <span className="text-xs opacity-75">{tab.count !== undefined ? `(${tab.count})` : ''}</span>
            </button>
            ))}
              </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 pb-20">
        {activeTab === 'academic' && <AdminAcademicPanel />}
        {activeTab === 'classes' && (
          <>
            {(() => {
              const activeClasses = classes.filter(c => c.active !== false);
              const inactiveClasses = classes.filter(c => c.active === false);
              
              const renderClassCard = (classItem) => (
                <div key={classItem.code} className={`bg-white rounded-lg p-4 shadow-sm ${classItem.active === false ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-neutral-900 truncate">{classItem.name}</h3>
                        {classItem.active === false && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-neutral-200 text-neutral-600 rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500">Code: {classItem.code}</p>
                    </div>
            <button
                      onClick={() => openEditClassModal(classItem)}
                      className="p-1 text-neutral-400 hover:text-neutral-600"
                      title="Edit"
                    >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
            </button>
          </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <p className="text-xs text-neutral-500">Students</p>
                      <p className="font-bold text-sm text-neutral-900">{classItem.stats.studentCount}</p>
        </div>
                    <div>
                      <p className="text-xs text-neutral-500">Assignments</p>
                      <p className="font-bold text-sm text-neutral-900">{classItem.stats.assignmentCount}</p>
      </div>
                      <div>
                      <p className="text-xs text-neutral-500">Avg Grade</p>
                      <p className="font-bold text-sm text-neutral-900">{classItem.stats.classAverage}%</p>
                      </div>
                    <div>
                      <p className="text-xs text-neutral-500">Attendance</p>
                      <p className="font-bold text-sm text-neutral-900">{classItem.stats.attendancePercentage}%</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-3 border-t border-neutral-100">
                        <button
                          onClick={() => handleClassSelect(classItem)}
                      className="flex-1 bg-neutral-100 text-neutral-700 px-3 py-2 rounded-lg text-xs font-medium"
                        >
                      Students
                        </button>
                        <button
                      onClick={() => navigate(`/level/${classItem.code}`)}
                      className="flex-1 bg-primary-600 text-white px-3 py-2 rounded-lg text-xs font-medium"
                        >
                          View Class
                        </button>
                      </div>
          </div>
              );

              return (
                <>
                  {/* Active Classes */}
                  {activeClasses.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-lg font-bold text-neutral-900 mb-4">Active Classes</h2>
                      <div className="space-y-3">
                        {activeClasses.map(renderClassCard)}
        </div>
                    </div>
                  )}

                  {/* Inactive Classes */}
                  {inactiveClasses.length > 0 && (
                    <div>
                      <h2 className="text-lg font-bold text-neutral-500 mb-4">Inactive Classes</h2>
                      <div className="space-y-3">
                        {inactiveClasses.map(renderClassCard)}
                      </div>
                    </div>
                  )}

        {/* Empty State */}
        {classes.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-neutral-900 mb-2">No Classes Found</h3>
            <p className="text-neutral-500">There are no classes to display in the admin dashboard.</p>
          </div>
        )}
                </>
              );
            })()}
          </>
        )}

        {activeTab === 'students' && (
          <div className="space-y-4">
            {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm"
                />
            </div>

            {/* Students Cards */}
            <div className="space-y-3">
              {filteredStudents.length === 0 ? (
                <div className="bg-white rounded-lg p-8 text-center shadow-sm">
                  <p className="text-neutral-500">
                    {searchTerm ? 'No students match your search.' : 'No students found.'}
                  </p>
                </div>
              ) : (
                filteredStudents.map((student) => (
                      <StudentRow 
                        key={student.id} 
                        student={student} 
                        onEdit={openEditModal}
                        getStudentEnrollments={getStudentEnrollments}
                      />
                ))
              )}
              </div>
            </div>
        )}

        {activeTab === 'parents' && (
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              <input
                type="text"
                placeholder="Search parents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm"
              />
            </div>

            {/* Parent Numbers List */}
            <div className="space-y-3">
              {filteredParentNumbers.length === 0 ? (
                <div className="bg-white rounded-lg p-8 text-center shadow-sm">
                <p className="text-neutral-500">
                    {searchTerm ? 'No parents match your search.' : 'No parents found.'}
                </p>
              </div>
              ) : (
                filteredParentNumbers.map((parentGroup) => {
                  const isExpanded = expandedParentNumbers.has(parentGroup.phoneNumber);
                  return (
                    <div key={parentGroup.phoneNumber} className="bg-white rounded-lg shadow-sm overflow-hidden">
                      {/* Clickable Header */}
                      <div 
                        className="flex justify-between items-center px-4 py-3 cursor-pointer active:bg-neutral-50"
                        onClick={() => toggleParentNumber(parentGroup.phoneNumber)}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <svg 
                            className={`w-4 h-4 text-neutral-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="font-medium text-sm text-neutral-900">{parentGroup.phoneNumber}</span>
                        </div>
                        <div className="text-xs font-medium text-primary-600">
                          {parentGroup.students.length} student{parentGroup.students.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                      
                      {/* Collapsible Content */}
                      {isExpanded && (
                        <div className="px-4 pb-3 border-t border-neutral-100">
                          <div className="space-y-2 mt-3">
                            {parentGroup.students.map((student) => (
                              <div key={student.id} className="bg-neutral-50 rounded-lg p-3">
                                <div className="font-medium text-sm text-neutral-900">{student.name}</div>
                                <div className="text-xs text-neutral-500 mt-1">
                                  {student.code || ''} {student.code && '•'} {
                                    student.parentType === 'Both' ? 'Both' :
                                    student.parentType === 'parent1' ? 'Primary' : 'Secondary'
                                  }
                                </div>
                                {student.code && (
                                  <div className="text-xs text-neutral-500 mt-1">Code: {student.code}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Search Bar and Add Button */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm"
                />
              </div>
              <button
                onClick={() => {
                  setUserEditData({
                    name: '',
                    email: '',
                    role: 'instructor',
                    code: '',
                    phone_number: ''
                  });
                  setShowAddUserModal(true);
                }}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
              >
                + Add
              </button>
            </div>

            {/* Users Cards */}
            <div className="space-y-3">
              {nonStudentUsers.filter(user =>
                user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.role?.toLowerCase().includes(searchTerm.toLowerCase())
              ).length === 0 ? (
                <div className="bg-white rounded-lg p-8 text-center shadow-sm">
                  <p className="text-neutral-500">
                    {searchTerm ? 'No users match your search.' : 'No users found.'}
                  </p>
                </div>
              ) : (
                nonStudentUsers.filter(user =>
                  user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  user.role?.toLowerCase().includes(searchTerm.toLowerCase())
                ).map((user) => (
                  <div key={user.id} className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-semibold text-sm">
                            {user.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-sm text-neutral-900 truncate">{user.name}</p>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              user.role === 'admin' 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {user.role}
                            </span>
                          </div>
                          {user.email && (
                            <p className="text-xs text-neutral-500 truncate mt-1">{user.email}</p>
                          )}
                          {user.phone_number && (
                            <p className="text-xs text-neutral-500 mt-1">{user.phone_number}</p>
                          )}
                          {user.code && (
                            <p className="text-xs text-neutral-500 mt-1">Code: {user.code}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => openEditUserModal(user)}
                        className="text-primary-600 text-sm font-medium px-3 py-1"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Student Summary Modal */}
        {showStudentSummary && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900">{selectedClass?.name} - Student Summary</h3>
                    <p className="text-sm text-neutral-500">Class Code: {selectedClass?.code}</p>
                  </div>
                  <button
                    onClick={handleBackToOverview}
                    className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 loading-spinner mx-auto mb-4"></div>
                    <p className="text-neutral-600">Loading student data...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-neutral-50 border-b border-neutral-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Student</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">Grade Average</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">Attendance</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">Present</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">Excused</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">Late</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">Absent</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">Total Days</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-neutral-200">
                        {students.map((student) => {
                          const stats = studentStats[student.id] || {};
                          return (
                            <tr key={student.id} className="hover:bg-neutral-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                                    <span className="text-white font-semibold text-xs">
                                      {student.name?.charAt(0)?.toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="ml-3">
                                    <div className="text-sm font-medium text-neutral-900">{student.name}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getClassAverageColor(stats.gradeAverage || 0)}`}>
                                  {stats.gradeAverage || 0}%
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getAttendanceColor(stats.attendancePercentage || 0)}`}>
                                  {stats.attendancePercentage || 0}%
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-green-700">{stats.attendanceStats?.present || 0}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-blue-700">{stats.attendanceStats?.excused || 0}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-yellow-700">{stats.attendanceStats?.late || 0}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-red-700">{stats.attendanceStats?.absent || 0}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-neutral-900">{stats.totalAttendance || 0}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {students.length === 0 && !loading && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-neutral-900 mb-2">No Students Found</h3>
                    <p className="text-neutral-500">There are no students enrolled in this class.</p>
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleBackToOverview}
                    className="btn-secondary px-4 py-2"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Student Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-neutral-900">Edit Student</h3>
                  <button
                    onClick={closeEditModal}
                    className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleStudentUpdate(editData);
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => setEditData({...editData, name: e.target.value})}
                      className="input w-full"
                      placeholder="Enter student's full name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({...editData, email: e.target.value})}
                      className="input w-full"
                      placeholder="Enter email address"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Student Code
                    </label>
                    <input
                      type="text"
                      value={editData.code}
                      onChange={(e) => setEditData({...editData, code: e.target.value})}
                      className="input w-full"
                      placeholder="Enter student code"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={editData.phone_number}
                      onChange={(e) => setEditData({...editData, phone_number: e.target.value})}
                      className="input w-full"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={editData.date_of_birth}
                      onChange={(e) => setEditData({...editData, date_of_birth: e.target.value})}
                      className="input w-full"
                    />
                  </div>

                  <div className="flex items-center gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary flex-1"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={closeEditModal}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Add User Modal */}
        {showAddUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-neutral-900">Add User</h3>
                  <button
                    onClick={() => {
                      setShowAddUserModal(false);
                      setUserEditData({});
                    }}
                    className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleAddUser(userEditData);
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={userEditData.name}
                      onChange={(e) => setUserEditData({...userEditData, name: e.target.value})}
                      className="input w-full"
                      placeholder="Enter full name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={userEditData.email}
                      onChange={(e) => setUserEditData({...userEditData, email: e.target.value})}
                      className="input w-full"
                      placeholder="Enter email address"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Role
                    </label>
                    <select
                      value={userEditData.role}
                      onChange={(e) => setUserEditData({...userEditData, role: e.target.value})}
                      className="input w-full"
                      required
                    >
                      <option value="instructor">Instructor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Access Code
                    </label>
                    <input
                      type="text"
                      value={userEditData.code}
                      onChange={(e) => setUserEditData({...userEditData, code: e.target.value})}
                      className="input w-full"
                      placeholder="Enter access code (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={userEditData.phone_number}
                      onChange={(e) => setUserEditData({...userEditData, phone_number: e.target.value})}
                      className="input w-full"
                      placeholder="Enter phone number (optional)"
                    />
                  </div>

                  <div className="flex items-center gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary flex-1"
                    >
                      {isSubmitting ? 'Adding...' : 'Add User'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddUserModal(false);
                        setUserEditData({});
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-neutral-900">Edit User</h3>
                  <button
                    onClick={closeEditUserModal}
                    className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdateUser(userEditData);
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={userEditData.name}
                      onChange={(e) => setUserEditData({...userEditData, name: e.target.value})}
                      className="input w-full"
                      placeholder="Enter full name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={userEditData.email}
                      onChange={(e) => setUserEditData({...userEditData, email: e.target.value})}
                      className="input w-full"
                      placeholder="Enter email address"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Role
                    </label>
                    <select
                      value={userEditData.role}
                      onChange={(e) => setUserEditData({...userEditData, role: e.target.value})}
                      className="input w-full"
                      required
                    >
                      <option value="instructor">Instructor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Access Code
                    </label>
                    <input
                      type="text"
                      value={userEditData.code}
                      onChange={(e) => setUserEditData({...userEditData, code: e.target.value})}
                      className="input w-full"
                      placeholder="Enter access code (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={userEditData.phone_number}
                      onChange={(e) => setUserEditData({...userEditData, phone_number: e.target.value})}
                      className="input w-full"
                      placeholder="Enter phone number (optional)"
                    />
                  </div>

                  <div className="flex items-center gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary flex-1"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={closeEditUserModal}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Class Modal */}
        {showEditClassModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-neutral-900">Edit Class Metadata</h3>
                  <button
                    onClick={closeEditClassModal}
                    className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdateClass(classEditData);
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Class Name
                    </label>
                    <input
                      type="text"
                      value={classEditData.name}
                      onChange={(e) => setClassEditData({...classEditData, name: e.target.value})}
                      className="input w-full"
                      placeholder="Enter class name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Class Code
                    </label>
                    <input
                      type="text"
                      value={classEditData.code}
                      className="input w-full bg-neutral-100"
                      disabled
                      readOnly
                    />
                    <p className="text-xs text-neutral-500 mt-1">Class code cannot be changed</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Period
                    </label>
                    <input
                      type="text"
                      value={classEditData.period}
                      onChange={(e) => setClassEditData({...classEditData, period: e.target.value})}
                      className="input w-full"
                      placeholder="Enter period (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Pass Code
                    </label>
                    <input
                      type="text"
                      value={classEditData.pass_code}
                      onChange={(e) => setClassEditData({...classEditData, pass_code: e.target.value})}
                      className="input w-full"
                      placeholder="Enter pass code (optional)"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Optional pass code for class access</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Primary Teacher
                    </label>
                    <select
                      value={classEditData.teacher_id || ''}
                      onChange={(e) => setClassEditData({...classEditData, teacher_id: e.target.value ? parseInt(e.target.value) : null})}
                      className="input w-full"
                    >
                      <option value="">Select primary teacher (optional)</option>
                      {nonStudentUsers
                        .filter(user => user.role === 'instructor' || user.role === 'admin')
                        .map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} {user.role === 'admin' ? '(Admin)' : ''}
                          </option>
                        ))}
                    </select>
                    <p className="text-xs text-neutral-500 mt-1">Select the primary instructor for this class</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Secondary Teacher
                    </label>
                    <select
                      value={classEditData.secondary_teacher_id || ''}
                      onChange={(e) => setClassEditData({...classEditData, secondary_teacher_id: e.target.value ? parseInt(e.target.value) : null})}
                      className="input w-full"
                    >
                      <option value="">Select secondary teacher (optional)</option>
                      {nonStudentUsers
                        .filter(user => (user.role === 'instructor' || user.role === 'admin') && user.id !== classEditData.teacher_id)
                        .map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} {user.role === 'admin' ? '(Admin)' : ''}
                          </option>
                        ))}
                    </select>
                    <p className="text-xs text-neutral-500 mt-1">Select an optional secondary instructor for this class</p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={classEditData.active !== false}
                        onChange={(e) => setClassEditData({...classEditData, active: e.target.checked})}
                        className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-neutral-700">Active</span>
                    </label>
                    <p className="text-xs text-neutral-500 mt-1 ml-6">Uncheck to mark this class as inactive</p>
                  </div>

                  <div className="flex items-center gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary flex-1"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={closeEditClassModal}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Student Row Component (Card for mobile)
function StudentRow({ student, onEdit, getStudentEnrollments }) {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [showEnrollments, setShowEnrollments] = useState(false);

  const fetchEnrollments = async () => {
    if (showEnrollments && enrollments.length === 0) {
      setLoadingEnrollments(true);
      try {
        const data = await getStudentEnrollments(student.id);
        setEnrollments(data);
      } catch (err) {
        console.error('Error fetching enrollments:', err);
      } finally {
        setLoadingEnrollments(false);
      }
    }
  };

  useEffect(() => {
    fetchEnrollments();
  }, [showEnrollments]);

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-semibold text-sm">
              {student.name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-neutral-900 truncate">{student.name}</div>
            {student.email && (
              <div className="text-xs text-neutral-500 truncate mt-1">{student.email}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/dashboard/transcript/${student.id}`)}
            className="text-neutral-600 text-sm font-medium px-3 py-1"
          >
            Transcript
          </button>
          <button
            onClick={() => onEdit(student)}
            className="text-primary-600 text-sm font-medium px-3 py-1"
          >
            Edit
          </button>
        </div>
        </div>
      
      <div className="space-y-2 text-xs text-neutral-500">
          {student.code && <div>Code: {student.code}</div>}
        {student.phone_number && <div>Phone: {student.phone_number}</div>}
          {student.date_of_birth && (
            <div>DOB: {new Date(student.date_of_birth).toLocaleDateString()}</div>
          )}
          <button
            onClick={() => setShowEnrollments(!showEnrollments)}
          className="text-primary-600 hover:text-primary-700 font-medium"
          >
            {showEnrollments ? 'Hide' : 'Show'} Classes
          </button>
          {showEnrollments && (
            <div className="mt-2 space-y-1">
              {loadingEnrollments ? (
                <div className="text-xs text-neutral-400">Loading...</div>
              ) : (
                <>
                  {enrollments.map((enrollment) => (
                    <div key={enrollment.level_code} className="text-xs bg-neutral-100 px-2 py-1 rounded">
                      {enrollment.level?.name} ({enrollment.level_code})
                    </div>
                  ))}
                  {enrollments.length === 0 && (
                    <div className="text-xs text-neutral-400">No classes enrolled</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
    </div>
  );
}
